/**
 * DisclosureEngine — resolves the list of required disclosure templates for a
 * RAO cart.
 *
 * Ported from legacy SPFx `services/DisclosureEngine.ts` + PackageBuilder
 * variant-selection logic.  The legacy service read `ao_Disclosures` (SP list);
 * v3 reads `config.disclosures` (sourced from SQL `DisclosureTemplates`) +
 * `config.entityRules[].requiredDocs` (per Bible §5.6).
 *
 * The engine is pure — it takes the cart state + config arrays and returns a
 * ranked list of templates without touching the network.  PackageBuilder is
 * the thing that reaches out to `/templates/compile` per template.
 *
 * Selection is driven by:
 *   - product categories / codes in the cart
 *   - entity type (ownership.entityType)
 *   - `config.entityRules[entityType].requiredDocs[]` — entity-specific docs
 *     (BO cert, trust certification, banking resolution, etc.)
 *
 * Signer roles are derived from cart.people — every person whose role is
 * `primary` or `joint` becomes a potential signer.  The engine returns the
 * roles per template based on `requiresSignature`.
 */
import type {
  DisclosureTemplate,
  EntityRule,
  EntityRuleRequiredDoc,
  Product,
} from '@retail-ops/shared-types';
import type { CustomerType } from '@retail-ops/shared-types/enums';
import type { RaoPerson } from '../types';

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/** A product line in the cart (shape we persist to `cart.productsJson`). */
export interface CartProductLine {
  code: string;
  qty?: number;
  cardTypeCode?: string;
  openingDeposit?: number;
  /** Reserved Core Director account number, stamped onto the product line by the
   * commit pipeline's AcctIdGen step (before signing). Present on the sign step;
   * absent before AcctIdGen runs. WT-BACKLOG C4 / TechReq#21. */
  acctId?: string;
}

export interface DisclosureEngineInput {
  /** Resolved entity type for the cart (ownership step). */
  entityType?: CustomerType;
  /** Products currently in the cart. */
  products: ReadonlyArray<CartProductLine>;
  /** People on the cart (role: primary/joint/etc). */
  people: ReadonlyArray<RaoPerson>;
  /** Branch code (for letterhead / footer merge fields). */
  branchCode?: string;
  /** Config.disclosures from ConfigContext. */
  disclosureTemplates: ReadonlyArray<DisclosureTemplate>;
  /** Config.entityRules from ConfigContext. */
  entityRules: ReadonlyArray<EntityRule>;
  /** Config.entityRequiredDocs — flat array of (customerType, docCode, isMandatory)
   *  triples. P0 fix 2026-04-27 — was nested on EntityRule.requiredDocs but
   *  the wire returns it as a sibling. */
  entityRequiredDocs?: ReadonlyArray<EntityRuleRequiredDoc>;
  /** Config.products — used to resolve product → category. */
  productCatalog: ReadonlyArray<Product>;
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

/** One resolved disclosure row — the thing PackageBuilder consumes. */
export interface ResolvedDisclosure {
  /** DisclosureTemplate.templateCode — stable identifier. */
  templateCode: string;
  /** Template display name (for preview UI). */
  title: string;
  /** Template category ('ALL' | 'DDA' | ...). */
  category?: string;
  /** Cart product code this disclosure applies to (if product-specific). */
  productCode?: string;
  /** Signer roles expected to sign this doc (e.g. ['Primary','Joint1']). */
  signerRoles: ReadonlyArray<string>;
  /** True if the template is non-waivable per entity rules / regulator. */
  required: boolean;
  /** True if the template flags `requiresSignature`. */
  requiresSignature: boolean;
  /** Provenance — what rule introduced this row ('product' | 'entity' | 'universal'). */
  source: 'product' | 'entity' | 'universal';
  /** Sort-order hint (DEP_AGREE=0, SIG_CARD=1, ...). */
  order: number;
}

// ---------------------------------------------------------------------------
// Ordering — canonical v3 DisclosureTemplate.code values (P0 fix 2026-04-27)
// ---------------------------------------------------------------------------
//
// Sort hint for the disclosure package. Lower numbers render first.
// When a template's `sortOrder` column is set, the engine prefers it over
// this static map. This map is the fallback for templates that haven't
// been admin-sorted via the disclosure-template editor yet.

const DOC_ORDER: Record<string, number> = {
  // ── Universal core (always first) ─────────────────────────────────
  DEP_AGREE:               0,
  PRIVACY:                 1,
  SIG_CARD:                2,
  // ── Product / regulatory layer ────────────────────────────────────
  REGCC:                   10,
  REGDD_TIS:               11,
  REGE_EFT:                12,
  RATE_SCHED:              13,
  ODP_A9:                  14,
  RIGHT_SETOFF:            15,
  ESIGN:                   16,
  CD_TIS:                  20,
  SDB_LEASE:               21,
  // ── CIP + identity ────────────────────────────────────────────────
  CIP_IND:                 30,
  CIP_BUS:                 31,
  W9:                      35,
  // ── Entity layer (BO + CDD + Banking Resolution) ──────────────────
  CDD_FORM:                40,
  BO_FORM:                 41,
  BANKING_RES:             42,
  // ── IRA wave (per plan-code variants) ─────────────────────────────
  IRA_APP_TRAD:            50,
  IRA_APP_ROTH:            51,
  IRA_APP_SEP:             52,
  IRA_CUSTODIAL_TRAD:      55,
  IRA_CUSTODIAL_ROTH:      56,
  IRA_CUSTODIAL_SEP:       57,
  IRA_DISC_TRAD:           60,
  IRA_DISC_ROTH:           61,
  IRA_DISC_SEP:            62,
  IRA_FIN_DISC:            65,
  IRA_CONTRIB_ELIG_TRAD:   70,
  IRA_CONTRIB_ELIG_ROTH:   71,
  IRA_SEP_PLAN:            75,
  IRA_BENEF_DESIG:         80,
  IRA_RECURRING:           81,
  IRA_RMD_NOTICE:          82,
  IRA_INHERITED_RMD_FORM:  83,
  IRA_ROLLOVER_ACK:        84,
  IRA_TRANSFER_IN:         85,
  IRA_529_TO_ROTH:         86,
  // ── Workflow-fired (Death Notice, Adverse Action) ─────────────────
  ADVERSE:                 90,
  'DN-PROC':               92,
  'DN-1ST':                93,
  'DN-2ND':                94,
  'DN-CLAIM':              95,
  'DN-RHH':                96,
};

function orderFor(code: string, sortOrder?: number | null): number {
  // Admin-set sortOrder takes precedence when present.
  if (typeof sortOrder === 'number') return sortOrder;
  if (DOC_ORDER[code] !== undefined) return DOC_ORDER[code];
  // Prefix match for forward-compat (so a future SIG_CARD.JOINT ranks with SIG_CARD).
  const prefixKey = Object.keys(DOC_ORDER).find((k) => code.startsWith(k));
  return prefixKey !== undefined ? DOC_ORDER[prefixKey] : 999;
}

/** Derive a boolean signature flag from the canonical SignatureRequirement enum. */
function templateRequiresSignature(tpl: DisclosureTemplate): boolean {
  return tpl.signatureRequirement !== 'none' && tpl.signatureRequirement !== undefined;
}

/**
 * Which signer roles is this template actually assigned to?
 *
 * Mirrors DisclosureRenderOrchestrator.ResolveSignerRolesForRequirement on the server.
 * Bill 2026-07-15: `requiresSignature` is a per-DOCUMENT flag, and both sides treated
 * it as if it answered a per-SIGNER question — so ODP_A9 ('customerRequired', Primary
 * only) listed "Primary, Joint1" in the package panel, the FSR handed the OD form to
 * the joint owner, and the signature they gave was never stamped.
 *
 * Bill's rulings 2026-07-15 — his words, not inferences:
 *   none                -> nobody
 *   bankOfficer         -> nobody (= the Processing FSR, not a customer; ADVERSE was
 *                          asking a declined customer to sign their own decline letter)
 *   customerRequired    -> the primary signer alone
 *   primaryOnly         -> alias of the above
 *   customer            -> the primary OWNER (15 templates, mostly IRA)
 *   customerAndOfficer  -> the primary customer (+ the processing FSR, who is not a
 *                          customer role and has no signing surface yet — see below)
 *   signature-required   -> the beneficial owner (W-8BEN-2014's own signature line);
 *                          DefaultSignatureCount = 1, so the single primary signer
 *   eachSigner          -> everyone (SIG_CARD etc.)
 *   anything else       -> everyone (safe default)
 *
 * This resolves CUSTOMER roles only. 'bankOfficer' and the officer half of
 * 'customerAndOfficer' have no ceremony surface today, so those signature lines render
 * blank — an open gap, not a regression.
 */
function resolveSignerRolesForRequirement(
  signatureRequirement: string | undefined,
  allSignerRoles: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const req = signatureRequirement?.trim();
  if (!req || req === 'none' || req === 'bankOfficer') return [];
  if (
    req === 'customerRequired' ||
    req === 'primaryOnly' ||
    req === 'customer' ||
    req === 'customerAndOfficer' ||
    req === 'signature-required'
  ) {
    const primary = allSignerRoles.find((r) => r.toLowerCase() === 'primary') ?? allSignerRoles[0];
    return primary ? [primary] : [];
  }
  return allSignerRoles;
}

// ---------------------------------------------------------------------------
// Signer roles
// ---------------------------------------------------------------------------

/**
 * Derive canonical SignNow signer roles from cart.people — matches the legacy
 * role canonicalization contract in signnow-architecture.md.
 *
 * Rules (from signnow-architecture.md §"Role Canonicalization Contract"):
 *   - Only letters/digits/apostrophe/period — no hyphens (Joint1, not Joint-1).
 *   - Primary=1, Joint1=2, Joint2=3, ... JointN=N+1
 *   - BankRep is an audit trail, NOT a SignNow signer — excluded here.
 */
export function deriveSignerRoles(people: ReadonlyArray<RaoPerson>): ReadonlyArray<string> {
  const roles: string[] = [];
  let jointSeq = 0;
  for (const person of people) {
    if (person.role === 'primary') {
      if (!roles.includes('Primary')) roles.push('Primary');
    } else if (person.role === 'joint') {
      jointSeq += 1;
      roles.push(`Joint${jointSeq}`);
    }
  }
  return roles;
}

// ---------------------------------------------------------------------------
// Main resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the disclosure list for a cart.  Pure function; safe to call on
 * every render.  Returns a DE-DUPED, SORTED list ready for PackageBuilder.
 */
export function resolveDisclosures(input: DisclosureEngineInput): ReadonlyArray<ResolvedDisclosure> {
  const {
    entityType,
    products,
    people,
    disclosureTemplates,
    entityRules,
    entityRequiredDocs,
    productCatalog,
  } = input;

  const signerRoles = deriveSignerRoles(people);

  // Build product-category + product-code sets from cart + catalog.
  // Scope by the product's CATEGORY LABEL (DDA/SAV/CD/IRA/BOX), upper-cased, so it
  // matches DisclosureTemplate.productCategory (migration V3_DisclosureProductScoping).
  // Was keyed off the numeric acctType, which never matched the label filter.
  const productCategories = new Set<string>();
  const productCodes = new Set<string>();
  for (const line of products) {
    if (!line.code) continue;
    productCodes.add(line.code.toUpperCase());
    const def = productCatalog.find((p) => p.code === line.code);
    if (def?.category) productCategories.add(def.category.toUpperCase());
  }

  // Keyed by template code so entity-required + product-required don't duplicate.
  const byCode = new Map<string, ResolvedDisclosure>();

  // --- Phase 1: templates the product / RAO surface wants ----------------
  // P0 fix 2026-04-27: realigned to canonical v3 wire shape — uses
  // `code` / `displayName` / `layer` / `signatureRequirement` instead of the
  // legacy SP `templateCode` / `templateName` / `category` / `productCodes` /
  // `requiresSignature`.
  for (const tpl of disclosureTemplates) {
    if (!tpl.isActive) continue;
    // FSR-officer audience only at this layer; RCP customer-portal templates
    // are filtered separately (Wave 5.5 RCP work).
    if (tpl.audience && tpl.audience !== 'fsr-officer') continue;
    if (!matchesProductLayer(tpl, productCategories, productCodes)) continue;
    if (!matchesEntityType(tpl, entityType)) continue;

    const requiresSignature = templateRequiresSignature(tpl);
    byCode.set(tpl.code, {
      templateCode: tpl.code,
      title: tpl.displayName,
      ...(tpl.layer ? { category: tpl.layer } : {}),
      signerRoles: resolveSignerRolesForRequirement(tpl.signatureRequirement, signerRoles),
      required: true,
      requiresSignature,
      source: tpl.layer === 'universal' ? 'universal' : 'product',
      order: orderFor(tpl.code, tpl.sortOrder ?? null),
    });
  }

  // --- Phase 2: entity-rule-driven templates -----------------------------
  // P0 fix 2026-04-27: requiredDocs is a flat sibling array on the wire
  // (ConfigFunction.cs §6 envelope), keyed by `customerType` + `docCode`.
  // Filter by the cart's entityType + verify the rule is active.
  if (entityType && entityRequiredDocs && entityRequiredDocs.length > 0) {
    const rule = entityRules.find((r) => r.customerType === entityType && r.isActive);
    if (rule) {
      const docsForType = entityRequiredDocs.filter((d) => d.customerType === entityType);
      for (const doc of docsForType) {
        // If the template is in the disclosure catalog we use it; otherwise
        // synthesize a stub so the UI can still show the row (admin may not
        // have authored the template yet, but the requirement is config-driven).
        const tpl = disclosureTemplates.find((t) => t.code === doc.docCode);
        const existing = byCode.get(doc.docCode);
        if (existing) {
          // Entity rule upgrades required-ness.
          byCode.set(doc.docCode, {
            ...existing,
            required: existing.required || doc.isMandatory,
            source: 'entity',
          });
        } else {
          const requiresSignature = tpl ? templateRequiresSignature(tpl) : true;
          byCode.set(doc.docCode, {
            templateCode: doc.docCode,
            title: tpl?.displayName ?? doc.docCode,
            ...(tpl?.layer ? { category: tpl.layer } : {}),
            // No catalog row = synthesized stub: the requirement is unknown, so present
            // to every signer rather than to none. Matches the server, which treats an
            // unresolvable template as "don't filter" — over-asking is recoverable,
            // silently assigning nobody would drop a required signature.
            signerRoles: tpl
              ? resolveSignerRolesForRequirement(tpl.signatureRequirement, signerRoles)
              : signerRoles,
            required: doc.isMandatory,
            requiresSignature,
            source: 'entity',
            order: orderFor(doc.docCode, tpl?.sortOrder ?? null),
          });
        }
      }
    }
  }

  // --- Phase 3: always-included universal templates ----------------------
  // Regulatory docs that apply to every retail opening — if they're in the
  // catalog but somehow didn't match the product filter, pull them in.
  const UNIVERSAL_CODES = ['DEP_AGREE', 'PRIVACY'];
  for (const code of UNIVERSAL_CODES) {
    if (byCode.has(code)) continue;
    const tpl = disclosureTemplates.find((t) => t.code === code && t.isActive);
    if (!tpl) continue;
    const requiresSignature = templateRequiresSignature(tpl);
    byCode.set(code, {
      templateCode: tpl.code,
      title: tpl.displayName,
      ...(tpl.layer ? { category: tpl.layer } : {}),
      signerRoles: resolveSignerRolesForRequirement(tpl.signatureRequirement, signerRoles),
      required: true,
      requiresSignature,
      source: 'universal',
      order: orderFor(tpl.code, tpl.sortOrder ?? null),
    });
  }

  // Sort: by sortOrder/DOC_ORDER, then by templateCode for stability.
  const out = Array.from(byCode.values());
  out.sort((a, b) => {
    const d = a.order - b.order;
    if (d !== 0) return d;
    return a.templateCode.localeCompare(b.templateCode);
  });

  return out;
}

// ---------------------------------------------------------------------------
// Template → cart matching by Layer (P0 fix 2026-04-27)
// ---------------------------------------------------------------------------
//
// Layer-driven include rules:
//   - `universal`   → always include
//   - `product`     → include when cart has any products (per-product filtering
//                     comes from Wave 5.5 WorkflowDisclosureRequirement table)
//   - `entity`      → handled in Phase 2 (entityRules.requiredDocs); skip here
//                     so we don't double-include
//   - `ownership`   → include — variant resolver picks the right body per
//                     CustomerType from DisclosureVariants
//   - `conditional` → skip (predicate-based, future Wave)
//   - `workflow`    → skip (fired from non-RAO surfaces — Death Notice,
//                     Adverse Action, RAM disclosure-trigger waves)

function matchesProductLayer(
  tpl: DisclosureTemplate,
  productCategories: Set<string>,
  productCodes: Set<string>,
): boolean {
  switch (tpl.layer) {
    case 'universal':
      // Cart-wide regulatory docs — almost all scoped 'ALL'; honor the filter
      // anyway in case a universal template is category-scoped.
      return matchesProductCategory(tpl, productCategories);
    case 'product':
      // Product-layer: REQUIRE the cart to actually hold a product in this
      // template's ProductCategory (and ApplicableProducts, if set). This is
      // the fix for the 33-docs bug — a DDA-only cart no longer pulls every
      // IRA/CD/SDB form (migration V3_DisclosureProductScoping backfilled the
      // per-template scope; the resolver just never applied it until now).
      return productCategories.size > 0
        && matchesProductCategory(tpl, productCategories)
        && matchesApplicableProducts(tpl, productCodes);
    case 'ownership':
      // Variant-resolved bodies — still scope by category so e.g. an IRA-only
      // ownership doc doesn't ride a DDA cart.
      return matchesProductCategory(tpl, productCategories);
    case 'entity':
      // Phase 2 handles entity-driven templates exclusively.
      return false;
    case 'conditional':
    case 'workflow':
      return false;
    default:
      // Unknown layer — fail closed.
      return false;
  }
}

/**
 * Parse a comma-separated scope CSV into an upper-cased Set, or `null` when the
 * scope is empty / 'ALL' (meaning "matches everything"). Shared by the three
 * product-scoping filters (migration V3_DisclosureProductScoping).
 */
function scopeSet(csv: string | null | undefined): Set<string> | null {
  const v = (csv ?? '').trim();
  if (v === '' || v.toUpperCase() === 'ALL') return null;
  return new Set(v.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean));
}

/** True when the cart holds a product in the template's ProductCategory ('ALL'/null ⇒ any). */
function matchesProductCategory(tpl: DisclosureTemplate, productCategories: Set<string>): boolean {
  const set = scopeSet(tpl.productCategory);
  if (set === null) return true;
  for (const c of productCategories) if (set.has(c)) return true;
  return false;
}

/** True when the cart holds one of the template's ApplicableProducts (null ⇒ all in category). */
function matchesApplicableProducts(tpl: DisclosureTemplate, productCodes: Set<string>): boolean {
  const set = scopeSet(tpl.applicableProducts);
  if (set === null) return true;
  for (const c of productCodes) if (set.has(c)) return true;
  return false;
}

/** True when the cart's entity type is in the template's ApplicableEntityTypes ('ALL'/null ⇒ any). */
function matchesEntityType(
  tpl: DisclosureTemplate,
  entityType: CustomerType | undefined,
): boolean {
  const set = scopeSet(tpl.applicableEntityTypes);
  if (set === null) return true;
  if (!entityType) return true; // unknown entity type → fail-open (don't over-exclude)
  return set.has(String(entityType).toUpperCase());
}
